import React from 'react';
import { DialogV2, Flex, TextV2, IconV2, Box, ButtonV2 } from '@withjoy/joykit';
import { CheckOutline, Warning } from '@withjoy/joykit/icons';
import { ReactComponent as JoyWalletIcon } from '@assets/wallet.svg';
import CreditCards from '@assets/credit-cards.png';
import PoweredByStripe from '@assets/PoweredbyStripe.png';
import { useTranslation } from '@shared/core';
import { Divider } from '../CreditCardSetup.styles';
import { useCreditCardSetupTelemetry } from '../CreditCardSetUp.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';
import { PayoutAccountInfo } from '../CreditCardSetUp.context';
import { withWindow } from '@shared/utils/withWindow';
import { NeedAssistance } from '../NeedAssistance';

interface IntroductionProps {
  isOwner: boolean;
  initKYCOnboarding: () => void;

  initPayoutAccountLoading: boolean;
  isValidUSAddress: boolean;
  payoutAccountInfo?: PayoutAccountInfo | null;
  handleInvalidShippingAddress?: () => void;
}

export const Introduction: React.FC<IntroductionProps> = ({
  isOwner,
  initKYCOnboarding,
  initPayoutAccountLoading,
  isValidUSAddress,
  payoutAccountInfo,
  handleInvalidShippingAddress
}) => {
  const telemetry = useCreditCardSetupTelemetry();
  const { t } = useTranslation('sharedRegistry');
  const { subtitle, letsGo, footerText, onlyForOwners, onlyForUS, continueText, stripeConnect, stripeDisclaimer, joyWalletTitle, joyWalletDescription } = t(
    'creditCardSetUpDialog'
  );
  useMount(() => {
    telemetry.creditCardSetupDialog.enter(payoutAccountInfo?.status || 'not-started');
  });
  return (
    <DialogV2.Body>
      <Flex flexDirection="column" alignItems="center" justifyContent="center">
        <Box position="relative" role="figure">
          <JoyWalletIcon />
        </Box>
        <TextV2 textAlign="center" color="mono14" typographyVariant="display2">
          {joyWalletTitle()}
        </TextV2>
        <Box marginY={6}>
          <img src={PoweredByStripe} title="Stripe" alt="stripe" />
        </Box>
      </Flex>
      <Flex flexDirection="column" alignItems="center">
        <TextV2 color="mono12" textAlign="center" typographyVariant="body3">
          {joyWalletDescription()}
        </TextV2>
        <Box marginTop={8}>
          <TextV2 color="mono14" typographyVariant="hed3">
            {subtitle()}
          </TextV2>
          <CreditCardSetupStepInformation />
        </Box>
      </Flex>
      <Divider />
      <Flex rowGap={3} alignItems="center" justifyContent="center">
        {isOwner && isValidUSAddress && (
          <ButtonV2
            loading={initPayoutAccountLoading}
            shape="rounded"
            intent="neutral"
            onClick={() => {
              if (!payoutAccountInfo) {
                telemetry.letsGetsStartedButtonClicked();
                initKYCOnboarding();
              } else {
                telemetry.kycContinueButtonClicked(payoutAccountInfo);
                withWindow(window => {
                  window.open(payoutAccountInfo.onboardingUrl || payoutAccountInfo.serviceDashboardUrl, '_self');
                });
              }
            }}
          >
            {!payoutAccountInfo ? letsGo() : continueText()}
          </ButtonV2>
        )}
      </Flex>
      <Flex flexDirection="column" alignItems="center" marginTop={8}>
        {payoutAccountInfo && isOwner && <NeedAssistance />}
        {!isOwner && (
          <Flex maxWidth="500px" rowGap={3} justifyContent="center" padding={5} borderRadius={3} borderWidth="1px" borderStyle="solid" borderColor="info3" marginY={3}>
            <IconV2 color="info5" marginRight={2}>
              <Warning />
            </IconV2>
            <TextV2 typographyVariant="body1">{onlyForOwners()}</TextV2>
          </Flex>
        )}

        {!isValidUSAddress && (
          <Flex maxWidth="500px" rowGap={3} justifyContent="center" padding={5} borderRadius={3} borderWidth="1px" borderStyle="solid" borderColor="info3" marginY={3}>
            <IconV2 color="info5" marginRight={2}>
              <Warning />
            </IconV2>
            <ButtonV2 intent="neutral" variant="link" typographyVariant="body1" size="sm" onClick={() => handleInvalidShippingAddress?.()}>
              {onlyForUS()}
            </ButtonV2>
          </Flex>
        )}
      </Flex>
      <Flex marginTop={8} flexDirection="column" alignItems="center" justifyContent="center">
        <TextV2 marginBottom={5} color="mono12" typographyVariant="label2">
          {footerText()}
        </TextV2>
        <figure>
          <img src={CreditCards} alt={footerText()} />
        </figure>
      </Flex>
      <Flex justifyContent="center" marginTop={8}>
        <Flex maxWidth="450px" flexDirection="column">
          <TextV2 textAlign="center" color="mono10" typographyVariant="label1">
            {stripeDisclaimer()}{' '}
            <ButtonV2 variant="link" intent="neutral" size="sm" as="a" href="https://stripe.com/legal/connect-account" typographyVariant="label1" fontWeight="bold" target="_blank">
              {stripeConnect()}
            </ButtonV2>
          </TextV2>
        </Flex>
      </Flex>
    </DialogV2.Body>
  );
};

const CreditCardSetupStepInformation: React.FC = () => {
  const { t } = useTranslation('sharedRegistry');
  const { recipient, bank, legalName } = t('creditCardSetUpDialog');
  return (
    <Box marginTop={5}>
      <SetupRequirement>{legalName()}</SetupRequirement>
      <SetupRequirement>{recipient()}</SetupRequirement>
      <SetupRequirement>{bank()}</SetupRequirement>
    </Box>
  );
};

const SetupRequirement: React.FC = ({ children }) => {
  return (
    <Flex as="li" paddingY={2}>
      <IconV2 marginRight={4}>
        <CheckOutline />
      </IconV2>
      <TextV2>{children}</TextV2>
    </Flex>
  );
};
